import { lazy, type ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter } from 'react-router-dom';
import { RootLayout } from './components/RootLayout';

const HomePage = lazy(() =>
  import('./pages/HomePage').then((module) => ({ default: module.HomePage }))
);
const MovieDetailPage = lazy(() =>
  import('./pages/MovieDetailPage').then((module) => ({
    default: module.MovieDetailPage,
  }))
);
const SearchPage = lazy(() =>
  import('./pages/SearchPage').then((module) => ({
    default: module.SearchPage,
  }))
);
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage').then((module) => ({
    default: module.ProfilePage,
  }))
);
const PurchasePage = lazy(() =>
  import('./pages/PurchasePage').then((module) => ({
    default: module.PurchasePage,
  }))
);
const NotificationListPage = lazy(() =>
  import('./pages/NotificationListPage').then((module) => ({
    default: module.NotificationListPage,
  }))
);
const TransactionHistoryPage = lazy(() =>
  import('./pages/TransactionHistoryPage').then((module) => ({
    default: module.TransactionHistoryPage,
  }))
);
const EpisodeSelectPage = lazy(() =>
  import('./pages/EpisodeSelectPage').then((module) => ({
    default: module.EpisodeSelectPage,
  }))
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((module) => ({
    default: module.NotFoundPage,
  }))
);
const HlsTestPage = lazy(() =>
  import('./pages/HlsTestPage').then((module) => ({
    default: module.HlsTestPage,
  }))
);
const TermsConditionsPage = lazy(() =>
  import('./pages/TermsConditionsPage').then((module) => ({
    default: module.TermsConditionsPage,
  }))
);

function RouteFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#06080d] text-sm text-white/70">
      {t('routes.loading')}
    </div>
  );
}

function withSuspense(element: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: withSuspense(<HomePage />) },
      { path: '/movie/:id', element: withSuspense(<MovieDetailPage />) },
      { path: '/search', element: withSuspense(<SearchPage />) },
      { path: '/profile', element: withSuspense(<ProfilePage />) },
      { path: '/purchase', element: withSuspense(<PurchasePage />) },
      {
        path: '/notifications',
        element: withSuspense(<NotificationListPage />),
      },
      {
        path: '/transactions',
        element: withSuspense(<TransactionHistoryPage />),
      },
      {
        path: '/movie/:id/episodes',
        element: withSuspense(<EpisodeSelectPage />),
      },
      {
        path: '/hls-test',
        element: withSuspense(<HlsTestPage />),
      },
      {
        path: '/terms-and-conditions',
        element: withSuspense(<TermsConditionsPage />),
      },
      {
        path: '*',
        element: withSuspense(<NotFoundPage />),
      },
    ],
  },
]);
